import { buildTimeline, type TimelineEntry, type TimelineInput } from './sessionTimeline';
import { trailKey, visibleLength, type SessionAction, type SessionPage, type SessionTrail } from './sessionTrail';

/**
 * A reproduction of a ticket (TOADO-925): what the person did, as numbered
 * steps in plain words and as a Playwright test, built from the timeline the
 * trail gives and nothing else. The same trail always gives the same text.
 *
 * Nothing the person typed or chose is in a trail, so every value is a named
 * placeholder ("what was typed into Email"). A click is found by its locator
 * where the timeline has one (role and name); otherwise the step says what is
 * known (a button in the header) and the test leaves a marked gap rather than
 * guessing a selector. What the page did after a step (a failed request, an
 * error) is said under it, so the test ends where the bug shows.
 *
 * Every string from the page reaches the test only as a JSON string literal,
 * and comments are one line of plain text: a page cannot write code here.
 */

export interface ReproductionStep {
  /** 1, 2, ... in order. */
  number: number;
  /** The step in plain words. */
  text: string;
  /** What the page did after it, in plain words, when anything failed. */
  then?: string;
}

export interface Reproduction {
  steps: ReproductionStep[];
  /** A Playwright test that walks the steps. */
  playwright: string;
  /** The placeholders the test asks to be filled in, in the order they appear. */
  placeholders: string[];
  /** Whether any step could not be found by a locator and was left as a gap. */
  gaps: number;
}

export interface ReproductionInput extends TimelineInput {
  /** The ticket's id, named in the test's title. */
  ticketId: string;
  /** Where it was captured, used when the trail has no page. */
  sourceUrl?: string;
  /**
   * Where the test opens pages (TOADO-948): an http or https origin with an
   * optional path, which the caller has checked. Each page keeps its own path,
   * query and fragment from the trail; the steps in words keep the address the
   * person was on.
   */
  baseUrl?: string;
}

/** The most steps a reproduction holds: the newest, as the timeline keeps the newest. */
export const MAX_REPRODUCTION_STEPS = 200;

export function buildReproduction(input: ReproductionInput): Reproduction {
  const trail = (input.session && typeof input.session === 'object' ? input.session : {}) as Partial<SessionTrail>;
  const pages = Array.isArray(trail.pages) ? trail.pages : [];
  const actions = Array.isArray(trail.actions) ? trail.actions : [];
  const timeline = buildTimeline(input);
  const onBase = (address: string) => rebased(address, input.baseUrl);
  const steps: ReproductionStep[] = [];
  const code: string[] = [];
  const placeholders: string[] = [];
  let gaps = 0;
  const step = (text: string, lines: string[], entry?: TimelineEntry) => {
    const number = steps.length + 1;
    const then = entry ? thenText(entry) : undefined;
    steps.push({ number, text, ...(then ? { then } : {}) });
    code.push(`  // ${number}. ${comment(text)}`, ...lines.map((lineOf) => `  ${lineOf}`));
    if (then) code.push(`  // Then ${comment(then)}.`);
  };

  const window = trail.setup?.window;
  if (window && isSize(window.width) && isSize(window.height)) {
    code.push(`  await page.setViewportSize({ width: ${Math.round(window.width)}, height: ${Math.round(window.height)} });`);
  }

  const entries = timeline.entries.filter((entry) => entry.kind === 'page' || entry.kind === 'action');
  if (!entries.some((entry) => entry.kind === 'page') && input.sourceUrl) {
    step(`Open ${input.sourceUrl}`, [`await page.goto(${literal(onBase(input.sourceUrl))});`]);
  }
  let opened = false;
  for (const entry of entries.slice(-MAX_REPRODUCTION_STEPS)) {
    if (entry.kind === 'page') {
      const page = pages[entry.index] as SessionPage | undefined;
      if (!page || typeof page.url !== 'string') continue;
      if (!opened || page.via === 'load' || page.via === 'back_forward' || page.via === undefined) {
        step(`${opened ? 'Go to' : 'Open'} ${page.url}`, [`await page.goto(${literal(onBase(page.url))});`]);
      } else if (page.via === 'reload') {
        step('Reload the page', ['await page.reload();']);
      } else {
        step(`The app moves to ${page.url}`, [`await expect(page).toHaveURL(${literal(onBase(page.url))});`]);
      }
      opened = true;
      continue;
    }
    const action = actions[entry.index] as SessionAction | undefined;
    if (!action) continue;
    switch (action.kind) {
      case 'click': {
        // One click per click recorded: a rage mark is on the click that made it three within a
        // second, whose earlier presses are steps of their own (Codex, review of TOADO-925).
        const what = entry.label.replace(/^(?:clicked|switched to) /, '');
        const text = `Click ${what}${action.rage === true ? ' again, quickly (three or more presses in a second)' : ''}${action.possiblyDead === true ? ' (it seemed to do nothing)' : ''}`;
        if (entry.locator) {
          step(text, [`await page.${entry.locator}.click();`], entry);
        } else {
          gaps += 1;
          step(text, ['// Not found by a locator: no name a locator can use was recorded. Find it on the page.'], entry);
        }
        break;
      }
      case 'change': {
        // A field is never found by a locator here: its recorded name is its label, placeholder or
        // name attribute, which need not be what any locator reads, and a role of `input` does not
        // say whether it is typed into or ticked (Codex, review of TOADO-925). The step says what is
        // known, the test marks a gap with a suggestion in a comment, and the value is a placeholder.
        const name = typeof action.name === 'string' && visibleLength(action.name) > 0 ? comment(action.name) : '';
        const how = Object.prototype.hasOwnProperty.call(FIELD_HOW, action.role) ? FIELD_HOW[action.role]! : FIELD_HOW.input!;
        const label = name ? `the ${JSON.stringify(name).slice(1, -1)} field` : 'a field';
        const placeholder = `${how.constant}_${placeholders.length + 1}`;
        placeholders.push(`${placeholder}: ${how.value} ${label}`);
        gaps += 1;
        step(`${how.verb} ${label}`, [
          name
            ? `// Not found by a locator: find the field named ${comment(JSON.stringify(name))} (its label, placeholder or name), then ${how.call}(${placeholder}).`
            : `// Not found by a locator: the field had no name the trail could keep. Find it on the page, then ${how.call}(${placeholder}).`,
        ], entry);
        break;
      }
      case 'key': {
        const key = trailKey(action.key);
        if (!key) break;
        // A key keeps no name and the trail does not say what had focus: the page itself, or a
        // control reached by keys it does not keep. So every key is a gap, never a press on
        // whatever has focus in the test (Codex, review of TOADO-925).
        gaps += 1;
        step(entry.label.replace(/^pressed /, 'Press '), [`// Not replayed: focus what the person pressed ${comment(key)} on (the page, or a control), then await page.keyboard.press(${comment(literal(key))}).`], entry);
        break;
      }
      case 'submit':
        // Sent by a click or a key the trail may not hold (Enter in a field is never kept), so the
        // test does not send it: a gap, as a field is (Codex, review of TOADO-925).
        gaps += 1;
        step(entry.label.replace(/^sent/, 'Send'), ['// Not replayed: the form was sent here, by a click or a key the trail may not name. Send it as the person did.'], entry);
        break;
      case 'resize':
        if (isSize(action.width) && isSize(action.height)) {
          step(`Resize the window to ${Math.round(action.width)}×${Math.round(action.height)}`, [`await page.setViewportSize({ width: ${Math.round(action.width)}, height: ${Math.round(action.height)} });`], entry);
        }
        break;
      default:
        break;
    }
  }

  const declarations = placeholders.map((placeholder) => {
    const [constant, ...rest] = placeholder.split(': ');
    return `const ${constant} = ${literal(`TODO: ${rest.join(': ')}`)};`;
  });
  const playwright = [
    `import { test, expect } from '@playwright/test';`,
    '',
    `// A reproduction of ticket ${comment(input.ticketId)}, from what the person did (Toado). Nothing they typed is known:`,
    '// fill in the values below. Steps no locator could find are marked; find those on the page.',
    ...declarations,
    ...(declarations.length > 0 ? [''] : []),
    `test(${literal(`reproduces ${input.ticketId}`)}, async ({ page }) => {`,
    ...code,
    '});',
    '',
  ].join('\n');
  return { steps, playwright, placeholders: placeholders.map((placeholder) => placeholder.split(': ').slice(1).join(': ')), gaps };
}

/** How a change to a field is put, by the role the trail recorded: only what that role says for certain. */
const FIELD_HOW: Readonly<Record<string, { verb: string; value: string; constant: string; call: string }>> = {
  textarea: { verb: 'Fill in', value: 'what was typed into', constant: 'VALUE', call: 'fill' },
  textbox: { verb: 'Fill in', value: 'what was typed into', constant: 'VALUE', call: 'fill' },
  searchbox: { verb: 'Fill in', value: 'what was typed into', constant: 'VALUE', call: 'fill' },
  select: { verb: 'Choose an option in', value: 'the option chosen in', constant: 'OPTION', call: 'selectOption' },
  combobox: { verb: 'Choose an option in', value: 'the option chosen in', constant: 'OPTION', call: 'selectOption' },
  listbox: { verb: 'Choose an option in', value: 'the option chosen in', constant: 'OPTION', call: 'selectOption' },
  input: { verb: 'Change', value: 'how the person changed', constant: 'CHANGE', call: 'fill, check or select' },
};

/** What failed after a step, in words, from the timeline's count of what followed it (TOADO-918). */
function thenText(entry: TimelineEntry): string | undefined {
  const followed = entry.followed;
  if (!followed || followed.failed === 0) return undefined;
  return followed.failures.length > 0 ? followed.failures.join('; ') : `${followed.failed} thing${followed.failed === 1 ? '' : 's'} failed`;
}

/**
 * An address moved onto the developer's base (TOADO-948): the base's origin
 * and path, then the address's own path, query and fragment. An address that
 * is not one stays as it was.
 */
function rebased(address: string, base: string | undefined): string {
  if (!base) return address;
  let url: URL;
  try { url = new URL(address); } catch { return address; }
  return `${base.replace(/\/+$/, '')}${url.pathname}${url.search}${url.hash}`;
}

/** A JavaScript string literal for any text: page text never becomes code. */
function literal(text: string): string {
  return JSON.stringify(text).replace(/\u2028/g, '\\u2028').replace(/\u2029/g, '\\u2029');
}

/** One line of plain text for a comment: no line breaks, no control characters. */
function comment(text: string): string {
  return text.replace(/[\u0000-\u001f\u007f\u2028\u2029]+/g, ' ').replace(/\s+/g, ' ').trim();
}

function isSize(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 && value < 100_000;
}
